import { mixed, object } from 'yup'
import { createDefaultSearchState } from '../defaults'
import type { IncludeExcludeMode, SearchFilterState } from '../types'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const sanitizeString = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

const sanitizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  if (typeof value === 'number') {
    if (value === 1) return true
    if (value === 0) return false
  }
  return false
}

const sanitizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

const sanitizeDate = (value: unknown): Date | null => {
  if (value == null) return null
  const parsed = value instanceof Date ? value : new Date(String(value))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const sanitizeDictionaryOptionArray = (
  value: unknown,
): SearchFilterState['productInformation']['countryOfUse'] => {
  if (!Array.isArray(value)) return []

  return value
    .map((item) => {
      if (!isRecord(item)) return null
      const label = sanitizeString(item.label)
      const valueCode = sanitizeString(item.valueCode)
      if (!label || !valueCode) return null
      return { label, valueCode }
    })
    .filter(
      (
        item,
      ): item is SearchFilterState['productInformation']['countryOfUse'][number] =>
        item !== null,
    )
}

const sanitizeIndustries = (value: unknown): Record<string, boolean> => {
  if (!isRecord(value)) return {}
  const entries = Object.entries(value).map(([key, item]) => [
    key,
    sanitizeBoolean(item),
  ])
  return Object.fromEntries(entries)
}

const filterStringSchema = mixed<string>()
  .transform((_value, originalValue) => sanitizeString(originalValue))
  .default('')

const filterBooleanSchema = mixed<boolean>()
  .transform((_value, originalValue) => sanitizeBoolean(originalValue))
  .default(false)

const filterStringArraySchema = mixed<string[]>()
  .transform((_value, originalValue) => sanitizeStringArray(originalValue))
  .default(() => [])

const filterDateSchema = mixed<Date | null>()
  .transform((_value, originalValue) => sanitizeDate(originalValue))
  .nullable()
  .default(null)

const providerModeSchema = mixed<IncludeExcludeMode>()
  .transform((_value, originalValue) => {
    const normalized = sanitizeString(originalValue).trim().toLowerCase()
    return normalized === 'exclude' ? 'exclude' : 'include'
  })
  .default('include')

const industriesSchema = mixed<Record<string, boolean>>()
  .transform((_value, originalValue) => sanitizeIndustries(originalValue))
  .default(() => ({}))

const dictionaryOptionArraySchema = mixed<
  SearchFilterState['productInformation']['countryOfUse']
>()
  .transform((_value, originalValue) =>
    sanitizeDictionaryOptionArray(originalValue),
  )
  .default(() => [])

export const searchStateSanitizerSchema = object({
  searchText: filterStringSchema,
  mediaType: object({
    document: filterBooleanSchema,
    image: filterBooleanSchema,
    video: filterBooleanSchema,
  }).default(() => createDefaultSearchState().mediaType),
  industries: industriesSchema,
  status: object({
    current: filterBooleanSchema,
    nonCurrent: filterBooleanSchema,
  }).default(() => createDefaultSearchState().status),
  documentInformation: object({
    mediaNumber: filterStringSchema,
    categories: filterStringArraySchema,
    productTypes: filterStringArraySchema,
    subIndustries: filterStringArraySchema,
  }).default(() => createDefaultSearchState().documentInformation),
  productInformation: object({
    engineModels: filterStringArraySchema,
    performanceNo: filterStringSchema,
    revisionNo: filterStringSchema,
    emissionTiers: filterStringArraySchema,
    targetRegions: filterStringArraySchema,
    duties: filterStringArraySchema,
    sources: filterStringArraySchema,
    fuelTypes: filterStringArraySchema,
    frequencies: filterStringArraySchema,
    rpms: filterStringArraySchema,
    voltages: filterStringArraySchema,
    applications: filterStringArraySchema,
    ratings: object({
      kva: filterStringSchema,
      bkw: filterStringSchema,
      bhp: filterStringSchema,
      ekw: filterStringSchema,
      mhp: filterStringSchema,
    }).default(() => createDefaultSearchState().productInformation.ratings),
    countryOfUse: dictionaryOptionArraySchema,
    GERP: filterBooleanSchema,
    CSQ: filterBooleanSchema,
    TIP: filterBooleanSchema,
    excitation: dictionaryOptionArraySchema,
    generatorArrangement: filterStringSchema,
    configurableSalesModel: filterStringSchema,
    featureCode: filterStringSchema,
    electricPowerSpecSizer: filterBooleanSchema,
    catDotComProductTechnicalInformation: filterBooleanSchema,
    appliesTo: filterStringSchema,
    powerDotCatDotCom: filterBooleanSchema,
  }).default(() => createDefaultSearchState().productInformation),
  advancedTextSearching: object({
    title: filterStringSchema,
    keyword: filterStringSchema,
    longDescription: filterStringSchema,
  }).default(() => createDefaultSearchState().advancedTextSearching),
  mediaDetails: object({
    securedFrom: filterDateSchema,
    securedTo: filterDateSchema,
    modifiedFrom: filterDateSchema,
    modifiedTo: filterDateSchema,
    languages: filterStringArraySchema,
    provider: filterStringArraySchema,
    providerMode: providerModeSchema,
    affiliationCode: filterStringSchema,
    productId: filterStringSchema,
  }).default(() => createDefaultSearchState().mediaDetails),
})

export const sanitizeSearchState = (value: unknown): SearchFilterState =>
  searchStateSanitizerSchema.cast(value, {
    stripUnknown: true,
  }) as SearchFilterState
